import { forwardRef, useId, type ComponentProps, type ReactNode } from 'react'
import { Select } from '@base-ui/react/select'
import { Combobox } from '@base-ui/react/combobox'
import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'
import { Switch as SwitchPrimitive } from '@base-ui/react/switch'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FieldContext, useFieldControl } from './field-context'

export interface Option {
  value: string
  label: string
  disabled?: boolean
}
export interface SelectFieldProps {
  value?: string
  onValueChange: (value: string) => void
  options: Option[]
  placeholder?: string
  disabled?: boolean
  name?: string
  id?: string
  searchable?: boolean
  className?: string
  'aria-label'?: string
}
const popupClass =
  'z-[1100] max-h-[min(360px,var(--available-height))] min-w-[var(--anchor-width)] overflow-auto rounded-lg border border-border bg-white p-1 text-sm text-foreground shadow-lg'
const optionClass =
  'flex cursor-default items-center justify-between gap-3 rounded px-3 py-2 outline-none data-highlighted:bg-accent data-disabled:opacity-40'
export function SelectField({
  value = '',
  onValueChange,
  options,
  placeholder = '請選擇',
  disabled,
  name,
  id,
  searchable,
  className,
  'aria-label': ariaLabel,
}: SelectFieldProps) {
  const field = useFieldControl()
  id = id ?? field.id
  if (searchable)
    return (
      <Combobox.Root
        items={options}
        value={options.find((item) => item.value === value) ?? null}
        onValueChange={(item) => onValueChange(item?.value ?? '')}
        itemToStringLabel={(item) => item.label}
        disabled={disabled}
        name={name}
      >
        <div className={cn('relative min-w-0', className)}>
          <Combobox.Input
            id={id}
            aria-invalid={field.invalid || undefined}
            aria-describedby={field.errorId}
            aria-label={ariaLabel}
            placeholder={placeholder}
            className="h-9 w-full rounded-lg border border-input bg-white py-1 pl-3 pr-9 text-sm outline-none focus:border-primary"
          />
          <Combobox.Trigger
            aria-label="展開選項"
            className="absolute inset-y-0 right-0 px-3"
          >
            <ChevronDown size={14} />
          </Combobox.Trigger>
        </div>
        <Combobox.Portal>
          <Combobox.Positioner sideOffset={4} className="z-[1100]">
            <Combobox.Popup className={popupClass}>
              <Combobox.Empty className="p-3 text-muted-foreground">
                沒有符合的選項
              </Combobox.Empty>
              <Combobox.List>
                {(item: Option) => (
                  <Combobox.Item
                    key={item.value}
                    value={item}
                    disabled={item.disabled}
                    className={optionClass}
                  >
                    {item.label}
                    <Combobox.ItemIndicator>
                      <Check size={14} />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                )}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
    )
  const emptyValue = '__kyren_select_empty__'
  const items = options.map((item) => ({
    ...item,
    value: item.value || emptyValue,
  }))
  return (
    <Select.Root
      value={value || (options.some((item) => !item.value) ? emptyValue : null)}
      onValueChange={(next) =>
        onValueChange(next === emptyValue ? '' : (next ?? ''))
      }
      items={items}
      disabled={disabled}
      name={name}
    >
      <Select.Trigger
        id={id}
        aria-invalid={field.invalid || undefined}
        aria-describedby={field.errorId}
        aria-label={ariaLabel}
        className={cn(
          'flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-input bg-white px-3 text-left text-sm outline-none focus-visible:border-primary disabled:opacity-50',
          className,
        )}
      >
        <Select.Value placeholder={placeholder} />
        <Select.Icon>
          <ChevronDown size={14} />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Positioner sideOffset={4} className="z-[1100]">
          <Select.Popup className={popupClass}>
            <Select.List>
              {items.map((item) => (
                <Select.Item
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  className={optionClass}
                >
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Check size={14} />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  )
}
export function Checkbox({ children, ...props }: CheckboxPrimitive.Root.Props) {
  const control = (
    <CheckboxPrimitive.Root
      {...props}
      className={cn(
        'inline-flex size-4 shrink-0 items-center justify-center rounded border border-input bg-white align-middle outline-none focus-visible:ring-2 focus-visible:ring-primary/30 data-checked:border-primary data-checked:bg-primary data-checked:text-white disabled:opacity-50',
        typeof props.className === 'string' ? props.className : undefined,
      )}
    >
      <CheckboxPrimitive.Indicator>
        <Check size={13} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
  return children ? (
    <label className="inline-flex items-start gap-2 text-sm">
      {control}
      <span>{children}</span>
    </label>
  ) : (
    control
  )
}
export function Switch(props: SwitchPrimitive.Root.Props) {
  return (
    <SwitchPrimitive.Root
      {...props}
      className={cn(
        'inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-slate-300 p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-primary/30 data-checked:bg-primary disabled:opacity-50',
        typeof props.className === 'string' ? props.className : undefined,
      )}
    >
      <SwitchPrimitive.Thumb className="size-4 rounded-full bg-white transition-transform data-checked:translate-x-4" />
    </SwitchPrimitive.Root>
  )
}
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  ComponentProps<'textarea'>
>(({ className, ...props }, ref) => {
  const field = useFieldControl()
  return (
    <textarea
      ref={ref}
      id={props.id ?? field.id}
      aria-invalid={field.invalid || undefined}
      aria-describedby={field.errorId}
      {...props}
      className={cn(
        'min-h-20 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-primary disabled:opacity-50',
        className,
      )}
    />
  )
})
Textarea.displayName = 'Textarea'
export function Field({
  label,
  error,
  htmlFor,
  children,
  required,
  className,
}: {
  label: ReactNode
  error?: ReactNode
  htmlFor?: string
  children: ReactNode
  required?: boolean
  className?: string
}) {
  const generated = useId()
  const id = htmlFor ?? generated
  const errorId = error ? `${id}-error` : undefined
  return (
    <FieldContext.Provider value={{ id, invalid: !!error, errorId }}>
      <div className={cn('grid min-w-0 gap-1.5', className)}>
        <label htmlFor={id} className="text-sm font-medium">
          {required && (
            <span aria-hidden="true" className="mr-1 text-destructive">
              *
            </span>
          )}
          {label}
        </label>
        {children}
        {error && (
          <p id={errorId} role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  )
}

function fixedAmount(value: string, precision: number): string {
  const [whole = '0', fraction = ''] = value.split('.')
  const scale = 10n ** BigInt(precision)
  let units =
    BigInt(whole || '0') * scale +
    BigInt((fraction + '0'.repeat(precision)).slice(0, precision) || '0')
  if (Number(fraction[precision] ?? '0') >= 5) units += 1n
  return fromUnits(units, precision)
}
function fromUnits(units: bigint, precision: number): string {
  if (precision === 0) return units.toString()
  const digits = units.toString().padStart(precision + 1, '0')
  return `${digits.slice(0, -precision)}.${digits.slice(-precision)}`.replace(
    /\.?0+$/,
    '',
  )
}
function amountUnits(value: string, precision: number): bigint {
  const [whole, fraction = ''] = fixedAmount(value || '0', precision).split('.')
  return (
    BigInt(whole || '0') * 10n ** BigInt(precision) +
    BigInt(fraction.padEnd(precision, '0') || '0')
  )
}
export function MoneyInput({
  value = '',
  onValueChange,
  onFocus,
  onBlur,
  onKeyDown,
  precision = 2,
  step = '0.01',
  min = '0',
  max,
  disabled,
  className,
  ...props
}: Omit<ComponentProps<'input'>, 'value' | 'onChange'> & {
  value?: string
  onValueChange: (value: string) => void
  precision?: number
}) {
  const field = useFieldControl()
  const increment = (direction: 1 | -1) => {
    if (disabled) return
    const delta = amountUnits(String(step === 'any' ? '0.01' : step), precision)
    let units = amountUnits(value, precision) + BigInt(direction) * delta
    const lower = amountUnits(String(min), precision)
    if (units < lower) units = lower
    if (max != null) {
      const upper = amountUnits(String(max), precision)
      if (units > upper) units = upper
    }
    onValueChange(fromUnits(units, precision))
  }
  return (
    <div className="relative min-w-0">
      <input
        id={props.id ?? field.id}
        aria-invalid={field.invalid || undefined}
        aria-describedby={field.errorId}
        {...props}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className={cn(
          'h-9 w-full min-w-0 rounded-lg border border-input bg-white py-1 pl-3 pr-9 text-sm text-foreground outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50',
          className,
        )}
        value={value}
        inputMode="decimal"
        type="text"
        onFocus={(event) => {
          onFocus?.(event)
        }}
        onBlur={(event) => {
          if (/^\d+(\.\d*)?$/.test(value))
            onValueChange(fixedAmount(value, precision))
          onBlur?.(event)
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault()
            increment(event.key === 'ArrowUp' ? 1 : -1)
          }
          onKeyDown?.(event)
        }}
        onChange={(event) => {
          const next = event.target.value.replace(/,/g, '')
          if (/^\d*(\.\d*)?$/.test(next)) onValueChange(next)
        }}
      />
      <div className="absolute inset-y-0 right-1 flex flex-col justify-center">
        <button
          type="button"
          disabled={disabled}
          aria-label="增加金額"
          className="rounded px-1 text-[10px] leading-3 text-muted-foreground hover:bg-muted disabled:opacity-30"
          onClick={() => increment(1)}
        >
          ▲
        </button>
        <button
          type="button"
          disabled={disabled}
          aria-label="減少金額"
          className="rounded px-1 text-[10px] leading-3 text-muted-foreground hover:bg-muted disabled:opacity-30"
          onClick={() => increment(-1)}
        >
          ▼
        </button>
      </div>
    </div>
  )
}
